import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  useTheme,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  useMediaQuery
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { tokens } from '../../theme';
import { getUserFromCookies } from '../../utils/Cookie-utils';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useClassExamService from '../../api/services/ClassExamsService';
import Table from '../../components/Table';
import Header from "../../components/Header";
import LoadingSpinner from '../../components/LoadingSpinner';
import CreateDialog from './Create_ClassExam';
import DeleteClassSubjectResults from './DeleteClassSubjectResults';

const ClassExams = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const { subjectId, teacherId, clazzId } = useParams();
  const { classExams, totalRecords, fetchClassExams } = useClassExamService();
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');


  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));


  const classTitle = location.state?.classTitle || '';


  const user = getUserFromCookies();
  const userRole = user?.data?.role;


  const canDelete = userRole === 'ADMIN' || userRole === 'MANAGER' || userRole === 'ACADEMIC';

  useEffect(() => {
    const loadData = async () => {
      if (teacherId && subjectId && clazzId) {
        setIsLoading(true);
        await fetchClassExams(teacherId, subjectId, clazzId);
        setIsLoading(false);
      }
    };
    loadData();
  }, [teacherId, subjectId, clazzId]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedExamType, setSelectedExamType] = useState(null);

  const openCreateDialog = () => setIsDialogOpen(true);
  const openDeleteDialog = (examType) => {

    if (!canDelete) {
      console.warn('User does not have permission to delete results');
      return;
    }
    setSelectedExamType(examType);
    setIsDeleteDialogOpen(true);
  };

  const openSubjectResultsPage = (row) => {
    const examinationTypeId = row.examTypeId ?? row.id;
    const examMarks = row.examMarks || '';
    const status = row.status || '';

    // Build URL with query parameters to persist values on reload
    const queryParams = new URLSearchParams();
    if (examMarks) queryParams.set('examMarks', examMarks);
    if (status) queryParams.set('status', status);

    const queryString = queryParams.toString();
    const url = `/subject_results/${teacherId}/${subjectId}/${clazzId}/${examinationTypeId}${queryString ? `?${queryString}` : ''}`;

    navigate(url);
  };

  // Filter exams based on search query
  const filteredExams = useMemo(() => {
    if (!classExams || classExams.length === 0) return [];

    const sorted = [...classExams].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    if (!searchQuery.trim()) return sorted;

    const query = searchQuery.toLowerCase();
    return sorted.filter((exam) => {
      const examinationType = (exam.examinationType || '').toLowerCase();
      const status = (exam.status || '').toLowerCase();
      const examMarks = (exam.examMarks || '').toString().toLowerCase();

      return (
        examinationType.includes(query) ||
        status.includes(query) ||
        examMarks.includes(query)
      );
    });
  }, [classExams, searchQuery]);

  const columns = [
    { field: "examinationType", headerName: "Examination Type", flex: 1, minWidth: 150 },
    { field: "status", headerName: "Status", flex: 1, minWidth: 100 },
    { field: "examMarks", headerName: "Exam-Marks", flex: 1, minWidth: 30 },
    {
      field: "actions",
      headerName: "Actions",
      width: canDelete ? 150 : 90,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" justifyContent="center" gap={1}>
          <IconButton color="success" onClick={() => openSubjectResultsPage(row)}>
            <VisibilityOutlinedIcon style={{ color: "green" }} />
          </IconButton>
          {canDelete && (
            <IconButton color="error" onClick={() => openDeleteDialog(row)}>
              <DeleteOutlinedIcon style={{ color: "red" }} />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box m="0px">
      <CreateDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={() => fetchClassExams(teacherId, subjectId, clazzId)}
        teacherId={teacherId}
        subjectId={subjectId}
        clazzId={clazzId}
      />

      {canDelete && (
        <DeleteClassSubjectResults
          open={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setSelectedExamType(null);
          }}
          onSuccess={() => fetchClassExams(teacherId, subjectId, clazzId)}
          examinationTypeId={selectedExamType?.examTypeId ?? selectedExamType?.id}
          subjectId={subjectId}
          clazzId={clazzId}
          teacherId={teacherId}
        />
      )}

      <Box p={2} ml={2} mr={2}>
        <Header
          title={`${classTitle ? `${classTitle} - ` : ""}${classExams?.[0]?.subjectName ?? ""} EXAMINATION RESULTS`}
        />

        <Box mb={2} display="flex" flexDirection={isSmallScreen ? "column" : "row"} gap={2} alignItems={isSmallScreen ? "stretch" : "center"}>
          <Button
            color="secondary"
            variant="contained"
            style={{ padding: "5px 8px", fontSize: "13px" }}
            onClick={openCreateDialog}
            sx={{ alignSelf: isSmallScreen ? "stretch" : "flex-start" }}
          >
            ADD EXAMS
          </Button>

          {/* Search Bar - Only shown on small screens */}
          {isSmallScreen && (
            <TextField
              fullWidth
              placeholder="Search by examination type, status, or marks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: colors.primary[400],
                },
              }}
            />
          )}
        </Box>

        {isLoading ? (
          <LoadingSpinner message="Loading exams..." height="250px" />
        ) : filteredExams.length > 0 ? (
          <>
            {isSmallScreen ? (
              // Card view for small screens
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredExams.map((exam) => (
                  <Card
                    key={exam.id}
                    sx={{
                      border: `1px solid ${colors.grey[300]}`,
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: colors.primary[400],
                    }}
                  >
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {/* Examination Type */}
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>
                            {exam.examinationType || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Status and Exam Marks in one row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                              Status
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.65rem',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                display: 'inline-block',
                                mt: 0.25
                              }}
                            >
                              {exam.status || 'N/A'}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              Marks:
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{ fontSize: '0.8rem', fontWeight: 'medium' }}
                            >
                              {exam.examMarks || 'N/A'}
                            </Typography>
                          </Box>

                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: '0.9rem' }} />}
                            onClick={() => openSubjectResultsPage(exam)}
                            sx={{ flex: 1, minWidth: '100px', fontSize: '0.75rem', py: 0.5, minHeight: '32px' }}
                          >
                            VIEW & EDIT & FILL RESULTS
                          </Button>
                          {canDelete && (
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              startIcon={<DeleteOutlinedIcon sx={{ fontSize: '0.9rem' }} />}
                              onClick={() => openDeleteDialog(exam)}
                              sx={{ flex: 1, minWidth: '100px', fontSize: '0.75rem', py: 0.5, minHeight: '32px' }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              // Table view for large screens
              <Table
                rows={filteredExams}
                columns={columns}
                totalRecords={filteredExams.length}
                colors={colors}
              />
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary">
              {searchQuery ? 'No exams found matching your search.' : 'No exams available.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ClassExams;
